import { useEffect, useState } from "react";
import { Loader2, RefreshCcw, Download, Clock } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("all");
  const [roleId, setRoleId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (userId !== "all") params.user_id = userId;
      if (roleId !== "all") params.role_id = roleId;
      if (statusFilter !== "all") params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/audit-logs", { params });
      setLogs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
        setUsers(u.data);
        setRoles(r.data);
      } catch (_) {}
      load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, roleId, statusFilter]);

  const exportCsv = () => {
    const header = ["Timestamp", "User", "Email", "Role", "Status", "IP", "Device", "Reason"];
    const rows = logs.map((l) => [
      new Date(l.login_time).toISOString(),
      l.user_name || "",
      l.email_attempted || "",
      l.role_name || "",
      l.status,
      l.ip_address || "",
      l.device || "",
      l.failure_reason || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `crewon-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Compliance</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
          Audit Logs
        </h1>
        <div className="text-sm text-slate-500 mt-1">
          Every login attempt is recorded forever — never auto-deleted.
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 flex flex-wrap items-end gap-3 border-b border-slate-100">
          <div>
            <Label className="text-xs">User</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="w-56 h-10 mt-1" data-testid="audit-log-user-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger className="w-56 h-10 mt-1" data-testid="audit-log-role-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.role_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-10 mt-1" data-testid="audit-log-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="logout">Logout</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date from</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 mt-1" data-testid="audit-log-date-from" />
          </div>
          <div>
            <Label className="text-xs">Date to</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 mt-1" data-testid="audit-log-date-to" />
          </div>
          <Button onClick={load} className="h-10 bg-[#0b192c] hover:bg-[#0f2544]" data-testid="audit-log-apply-btn">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Apply
          </Button>
          <Button onClick={exportCsv} variant="outline" className="h-10 ml-auto" data-testid="audit-log-export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full crew-table" data-testid="audit-logs-table">
            <thead>
              <tr>
                <th className="text-left">Timestamp</th>
                <th className="text-left">User</th>
                <th className="text-left">Role</th>
                <th className="text-left">Status</th>
                <th className="text-left">IP Address</th>
                <th className="text-left">Device</th>
                <th className="text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-10">No audit entries match these filters</td></tr>
              )}
              {!loading && logs.map((l) => (
                <tr key={l.id}>
                  <td className="text-xs text-slate-500 whitespace-nowrap">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(l.login_time).toLocaleString()}
                  </td>
                  <td className="font-medium text-slate-900">
                    {l.user_name || <span className="text-slate-400 italic">unknown</span>}
                    <div className="font-mono text-[11px] text-slate-500">{l.email_attempted}</div>
                  </td>
                  <td className="text-slate-700">{l.role_name || "—"}</td>
                  <td>
                    <span className={`chip ${l.status === "success" ? "chip-success" : l.status === "failed" ? "chip-danger" : "chip-info"}`}>
                      {l.status}
                    </span>
                  </td>
                  <td className="font-mono text-xs text-slate-600">{l.ip_address || "—"}</td>
                  <td className="text-slate-600">{l.device || "—"}</td>
                  <td className="text-slate-500 text-xs">{l.failure_reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
